// CSessionSummaryScene — 東南西北牌局組總結算。
//
// === 使用圖片 ===
// 無直接圖片 — 桌面由 createTableBackground（Phaser.Graphics）繪製，
// 排名列由 Graphics / Text 繪製，標題由 page-skin 繪製；街機按鈕經 dialog-skin 使用圖片，詳見各檔頭。

import Phaser from 'phaser';
import { reveal, pop, sweep, celebrate, reducedMotion } from '@ui/motion';
import { BGMTrack } from '@audio/audio-keys';
import { playUIClick, playUIReward, syncBackgroundMusic } from '@audio/scene-audio';

import { LOGICAL_WIDTH } from '@config/game-config';
import {
  GameSetCompletionReason,
  GameSetStatus,
  buildSessionStandings,
  type IGameSetSession,
} from '@core/index';
import { abandonActiveSession, createDefaultGameSave, loadGameSave } from '@storage/index';
import { abandonSet } from '@net/account-client';
import { isServerTableEnabled } from '@net/server-mode';
import { noticeFromError } from '@ui/notice-toast';
import { createTableBackground } from '@ui/table-background';
import { createPageTitle, createPageButton, PAGE_THEME } from '@ui/page-skin';

import { SceneKey } from './scene-keys';

const ROW_STAGGER_MS = 45; // 四列合計約 320ms
const ROW_ENTER_MS = 185; // 排名進場時間
const AWARD_DELAY_MS = 330; // 排名落定後強調結果
const PANEL_X = LOGICAL_WIDTH / 2;
const TITLE_Y = 108;
const SUBTITLE_Y = 157;
const SUBTITLE_FONT_SIZE = 22;
const HEADER_Y = 212;
const ROW_START_Y = 263;
const ROW_STEP_Y = 74;
const ROW_WIDTH = 884;
const ROW_HEIGHT = 58;
const ROW_COLOR = PAGE_THEME.row;
const ROW_WIN_COLOR = PAGE_THEME.selected;
const ROW_FONT_SIZE = 22;
const SUMMARY_Y = 558;
const BUTTON_Y = 610;
const BUTTON_WIDTH = 250;
const BUTTON_HEIGHT = 60;

const ROW_RADIUS = 10; // 排名列圓角
const COLUMN_LAYOUT = [
  { x: 236, origin: 0.5, label: '名次' },
  { x: 290, origin: 0, label: '玩家' },
  { x: 654, origin: 1, label: '起始籌碼' },
  { x: 826, origin: 1, label: '總輸贏' },
  { x: 1030, origin: 1, label: '最終籌碼' },
] as const;

export class CSessionSummaryScene extends Phaser.Scene {
  private preview = false;
  private leaving = false;

  constructor() {
    super({ key: SceneKey.SessionSummary });
  }

  create(): void {
    this.leaving = false;
    syncBackgroundMusic(this, BGMTrack.Lobby);
    this.preview =
      new URLSearchParams(window.location.search).get('scene') === SceneKey.SessionSummary;
    createTableBackground(this);

    const save = this.preview ? this.previewSave() : loadGameSave();
    const session = save.activeSession;
    if (!session) {
      if (!this.preview) this.scene.start(SceneKey.Home);
      return;
    }

    const standings = buildSessionStandings(session.startingBalances, save.ledger.balances);
    this.renderPanel(session);
    COLUMN_LAYOUT.forEach((column) =>
      this.add
        .text(column.x, HEADER_Y, column.label, {
          color: PAGE_THEME.goldText,
          fontFamily: PAGE_THEME.font,
          fontSize: `${ROW_FONT_SIZE}px`,
        })
        .setOrigin(column.origin, 0.5)
    );

    standings.forEach((standing, index) => {
      const previous = new Set(this.children.list);
      const y = ROW_START_Y + index * ROW_STEP_Y;
      const graphics = this.add.graphics();
      graphics.fillStyle(index === 0 ? ROW_WIN_COLOR : ROW_COLOR, 1);
      graphics.fillRoundedRect(
        PANEL_X - ROW_WIDTH / 2,
        y - ROW_HEIGHT / 2,
        ROW_WIDTH,
        ROW_HEIGHT,
        ROW_RADIUS
      );
      const player = session.players[standing.seat];
      const deltaText =
        standing.delta >= 0
          ? `+${standing.delta.toLocaleString()}`
          : standing.delta.toLocaleString();
      const values = [
        String(standing.rank),
        player?.name ?? '',
        standing.startingBalance.toLocaleString(),
        deltaText,
        standing.endingBalance.toLocaleString(),
      ];
      let personalDelta: Phaser.GameObjects.Text | undefined;
      COLUMN_LAYOUT.forEach((column, columnIndex) => {
        const text = this.add
          .text(column.x, y, values[columnIndex] ?? '', {
            color:
              columnIndex === 3
                ? standing.delta < 0
                  ? PAGE_THEME.negative
                  : PAGE_THEME.goldText
                : PAGE_THEME.text,
            fontFamily: PAGE_THEME.font,
            fontSize: `${ROW_FONT_SIZE}px`,
            fontStyle: index === 0 ? 'bold' : 'normal',
          })
          .setOrigin(column.origin, 0.5);
        if (standing.seat === session.playerSeat && columnIndex === 3) personalDelta = text;
      });
      const row = this.add.container(
        0,
        0,
        this.children.list.filter((child) => !previous.has(child))
      );
      reveal(this, row, index * ROW_STAGGER_MS, ROW_ENTER_MS);
      this.time.delayedCall(reducedMotion() ? 0 : AWARD_DELAY_MS, () => {
        if (personalDelta) pop(this, personalDelta);
        if (index === 0) {
          sweep(this, PANEL_X, y, ROW_WIDTH, ROW_HEIGHT);
          playUIReward(this);
          if (standing.seat === session.playerSeat && standing.delta > 0)
            celebrate(this, PANEL_X, y);
        }
      });
    });

    this.add
      .text(
        PANEL_X,
        SUMMARY_Y,
        `總局數 ${session.completedRounds}   完成圈數 ${session.completedCircles}`,
        {
          color: PAGE_THEME.goldText,
          fontFamily: PAGE_THEME.font,
          fontSize: '26px',
          fontStyle: 'bold',
        }
      )
      .setOrigin(0.5);
    this.renderCloseButton();
    this.bindKeyboard();
  }

  private renderPanel(session: IGameSetSession): void {
    createPageTitle(this, PANEL_X, TITLE_Y, '牌局總結算');
    const subtitle =
      session.completionReason === GameSetCompletionReason.InsufficientBalance
        ? '四家籌碼未達下一局門檻，牌局組結束'
        : '北風圈完成';
    this.add
      .text(PANEL_X, SUBTITLE_Y, subtitle, {
        color: PAGE_THEME.text,
        fontFamily: PAGE_THEME.font,
        fontSize: `${SUBTITLE_FONT_SIZE}px`,
      })
      .setOrigin(0.5);
  }

  private renderCloseButton(): void {
    const button = createPageButton(
      this,
      PANEL_X,
      BUTTON_Y,
      BUTTON_WIDTH,
      BUTTON_HEIGHT,
      '返回首頁'
    );
    button.setPrimary().setFocused(true);
    if (this.preview) return;
    const hit = this.add
      .zone(PANEL_X, BUTTON_Y, BUTTON_WIDTH, BUTTON_HEIGHT)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        playUIClick(this);
        this.returnHome();
      });
    button.bindInteraction(hit);
  }

  /** 結束這一將回首頁；伺服器模式要先讓伺服器清掉已結束的牌局組，否則回首頁同步後又會回來。 */
  private returnHome(): void {
    if (this.leaving) return;
    this.leaving = true;
    if (!isServerTableEnabled()) {
      abandonActiveSession();
      this.scene.start(SceneKey.Home);
      return;
    }
    abandonSet()
      .catch((error: unknown) => noticeFromError(error))
      .finally(() => this.scene.start(SceneKey.Home));
  }

  private bindKeyboard(): void {
    if (this.preview) return;
    const keyboard = this.input.keyboard;
    if (!keyboard) return;
    const returnHome = (event: KeyboardEvent): void => {
      if (
        event.key !== 'Enter' &&
        event.key !== ' ' &&
        event.key !== 'Spacebar' &&
        event.key !== 'Escape' &&
        event.key !== 'Backspace'
      )
        return;
      playUIClick(this);
      this.returnHome();
      event.preventDefault();
    };
    keyboard.on('keydown', returnHome);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => keyboard.off('keydown', returnHome));
  }

  private previewSave(): ReturnType<typeof createDefaultGameSave> {
    const save = createDefaultGameSave();
    const insufficient =
      new URLSearchParams(window.location.search).get('fixture') === 'insufficient';
    const session: IGameSetSession = {
      schemaVersion: 1,
      sessionId: 'preview-session',
      status: GameSetStatus.Completed,
      completionReason: insufficient
        ? GameSetCompletionReason.InsufficientBalance
        : GameSetCompletionReason.FinishedNorthCircle,
      players: [
        { id: 'player-self', name: '市民', seat: 0 },
        { id: 'npc-right', name: '史豔文', seat: 1 },
        { id: 'npc-opposite', name: '水手金星', seat: 2 },
        { id: 'npc-left', name: '無敵鐵金剛', seat: 3 },
      ],
      playerSeat: 0,
      fakeEastSeat: 0,
      initialDealerSeat: 2,
      dealerSeat: 2,
      prevailingWind: 'north',
      roundIndex: 4,
      dealerStreak: 0,
      completedRounds: 24,
      completedCircles: 4,
      startingBalances: [1_000_000, 1_000_000, 1_000_000, 1_000_000],
      bet: { base: 100, taiUnit: 20 },
      currentRound: null,
    };
    return {
      ...save,
      ledger: {
        ...save.ledger,
        balances: [997_400, 1_000_000, 1_002_600, 1_000_000],
      },
      activeSession: session,
    };
  }
}
